import React from 'react';
import { Check, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const InnovationsSection = () => {
  const navigate = useNavigate();
  const goToProduct = (hash) => navigate(`/products${hash}`);

  return (
    <section id="innovations" className="scroll-mt-16 min-h-screen py-20 lg:py-24 bg-white border-b border-slate-200 flex flex-col justify-center relative z-20">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 my-auto">
        {/* Header Lockup */}
        <div className="max-w-3xl mb-12 sm:mb-14">
          <span className="text-xs font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-2">DEAL OS&#8482; CAPABILITIES</span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-[#0F172A] tracking-tight mb-4">
            Our latest innovations
          </h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            Engineered through closed-door deal sprints with sovereign funds, private equity sponsors, and tier-one strategy practices across Abu Dhabi, Europe, and the US. Explore the core surfaces powering cognitive deal execution and strategic underwriting.
          </p>
        </div>

        {/* 4-Column Showcase Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">

          {/* Card 1: Deal OS */}
          <div onClick={() => goToProduct('#top')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && goToProduct('#top')} className="flex flex-col justify-between group cursor-pointer">
            <div className="w-full h-[380px] sm:h-[410px] rounded-2xl bg-gradient-to-b from-[#F7F8FA] via-[#ECEEF2] to-[#E2E5EB] border border-slate-200/80 group-hover:border-[#F58220]/50 group-hover:shadow-[0_16px_36px_-12px_rgba(245,130,32,0.18)] overflow-hidden relative flex items-center justify-center p-6 mb-5 transition-all duration-300">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.85)_0%,_transparent_75%)] pointer-events-none"></div>

              <div className="relative w-48 h-48 flex items-center justify-center">
                <div className="absolute bottom-4 w-36 h-12 bg-slate-400/25 blur-xl rounded-full transform scale-y-50"></div>
                <div className="relative w-36 h-36 flex flex-col items-center justify-center">
                  <div className="absolute inset-x-2 bottom-0 h-11 rounded-xl bg-white/40 border border-white/60 shadow-md backdrop-blur-md"></div>
                  <div className="absolute inset-x-2 bottom-3.5 h-11 rounded-xl bg-white/50 border border-white/70 shadow-md backdrop-blur-md"></div>
                  <div className="absolute inset-x-2 bottom-7 h-11 rounded-xl bg-white/60 border border-white/80 shadow-md backdrop-blur-md"></div>
                  <div className="absolute inset-x-2 bottom-[2.625rem] h-11 rounded-xl bg-white/75 border border-white/90 shadow-md backdrop-blur-md"></div>
                  <div className="absolute inset-x-1 bottom-14 h-16 rounded-2xl bg-white/90 group-hover:bg-white border border-white group-hover:border-[#F58220]/40 shadow-lg backdrop-blur-lg flex items-center justify-center text-slate-800 transition duration-300">
                    <div className="flex items-center gap-1 font-bold text-xl tracking-wider text-slate-800/80 group-hover:text-slate-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)] transition duration-300">
                      <span className="tracking-tighter">OS</span>
                      <span className="text-[10px] -mt-2 font-mono group-hover:text-[#F58220] transition duration-300">&#8482;</span>
                    </div>
                    <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/90 to-transparent rounded-t-2xl pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-[#0F172A] group-hover:text-[#F58220] transition-colors duration-200 mb-2">Deal OS&#8482;</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4">
                The foundational operating system facilitating the continuous flow of transaction context, multi-agent execution, and audit-grade decisioning across the entire deal lifecycle.
              </p>
            </div>
            <div className="pt-1 text-xs text-slate-500 group-hover:text-[#F58220] transition-colors flex items-center gap-1 font-sans">
              <span>&#8627; Read more</span>
            </div>
          </div>

          {/* Card 2: Workflows */}
          <div onClick={() => goToProduct('#workflows')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && goToProduct('#workflows')} className="flex flex-col justify-between group cursor-pointer">
            <div className="w-full h-[380px] sm:h-[410px] rounded-2xl bg-gradient-to-b from-[#F7F8FA] via-[#ECEEF2] to-[#E2E5EB] border border-slate-200/80 group-hover:border-[#F58220]/50 group-hover:shadow-[0_16px_36px_-12px_rgba(245,130,32,0.18)] overflow-hidden relative flex items-center justify-center p-6 mb-5 transition-all duration-300">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.85)_0%,_transparent_75%)] pointer-events-none"></div>

              <div className="relative w-52 h-52 flex items-center justify-center">
                <div className="absolute top-2 right-5 w-11 h-11 rounded-xl bg-white shadow-md border border-slate-100 flex items-center justify-center transform rotate-6 group-hover:rotate-12 transition duration-300">
                  <div className="w-6 h-6 rounded bg-[#107C41] flex items-center justify-center text-white text-[10px] font-bold">X</div>
                </div>

                <div className="absolute top-5 left-4 w-10 h-10 rounded-xl bg-white shadow-md border border-slate-100 flex items-center justify-center transform -rotate-12 group-hover:-rotate-6 transition duration-300">
                  <span className="font-black text-slate-800 text-[11px] tracking-tighter">PB</span>
                </div>

                <div className="absolute bottom-5 left-3 w-11 h-11 rounded-xl bg-white shadow-md border border-slate-100 flex items-center justify-center transform rotate-3 group-hover:-rotate-3 transition duration-300">
                  <span className="font-extrabold text-[#0D6B4F] text-[11px] tracking-tight">DS</span>
                </div>

                <div className="absolute bottom-5 right-4 w-11 h-11 rounded-xl bg-white shadow-md border border-slate-100 flex items-center justify-center transform -rotate-6 group-hover:rotate-3 transition duration-300">
                  <div className="w-6 h-6 rounded bg-[#C43E1C] flex items-center justify-center text-white text-[10px] font-bold">P</div>
                </div>

                <div className="absolute -top-2 left-[5.5rem] w-7 h-7 rounded-full bg-[#F58220]/20 border border-[#F58220]/40 flex items-center justify-center text-[#F58220] text-[10px] shadow-sm group-hover:scale-110 transition duration-300">
                  <Check className="w-3.5 h-3.5 text-[#F58220]" />
                </div>

                <div className="w-[5.5rem] h-[5.5rem] rounded-3xl bg-white/95 border border-white group-hover:border-[#F58220]/40 shadow-xl backdrop-blur-md flex items-center justify-center z-10 group-hover:scale-105 transition-all duration-300">
                  <svg className="w-10 h-10 text-slate-900 group-hover:text-[#F58220] transition-colors duration-200" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C12 6.627 6.627 12 0 12C6.627 12 12 17.373 12 24C12 17.373 17.373 12 24 12C17.373 12 12 6.627 12 0Z" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-[#0F172A] group-hover:text-[#F58220] transition-colors duration-200 mb-2">Workflows</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4">
                End-to-end execution of complex transaction workflows. The Agent plans, reconciles, stress-tests, and delivers, so you focus strictly on judgment.
              </p>
            </div>
            <div className="pt-1 text-xs text-slate-500 group-hover:text-[#F58220] transition-colors flex items-center gap-1 font-sans">
              <span>&#8627; Read more</span>
            </div>
          </div>

          {/* Card 3: Studio */}
          <div onClick={() => goToProduct('#studio')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && goToProduct('#studio')} className="flex flex-col justify-between group cursor-pointer">
            <div className="w-full h-[380px] sm:h-[410px] rounded-2xl bg-gradient-to-b from-[#F7F8FA] via-[#ECEEF2] to-[#E2E5EB] border border-slate-200/80 group-hover:border-[#F58220]/50 group-hover:shadow-[0_16px_36px_-12px_rgba(245,130,32,0.18)] overflow-hidden relative flex flex-col items-center pt-5 px-4 mb-5 transition-all duration-300">
              <div className="w-full bg-white rounded-2xl shadow-xl border border-black/5 group-hover:border-[#F58220]/30 p-4 space-y-3 font-sans transform group-hover:translate-y-1.5 transition duration-300">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">Cockpit Setup</span>
                  <span className="w-2 h-2 rounded-full bg-[#F58220]"></span>
                </div>

                <div>
                  <div className="text-[11px] font-semibold text-slate-800 mb-1.5">Thesis Rubrics</div>
                  <div className="space-y-1.5 text-[10px] text-slate-600">
                    <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Algorithmic Moat
                      </span>
                      <span className="font-mono text-slate-400">Tier-1 Cleared</span>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                        Addressable Profit Pools
                      </span>
                      <span className="font-mono text-slate-400">$14.2B TAM</span>
                    </div>
                    <div className="p-2 rounded-lg bg-[#FFF6EE] border border-[#F58220]/30 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium text-slate-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F58220]"></span>
                        Downside Risk Floor
                      </span>
                      <span className="font-mono text-[#F58220] font-bold">Bounded</span>
                    </div>
                  </div>
                </div>

                <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-slate-800">Deliberation Protocol</span>
                  <span className="text-[9px] text-[#F58220] font-mono font-semibold bg-[#F58220]/10 px-2 py-0.5 rounded">Bull vs Bear</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-[#0F172A] group-hover:text-[#F58220] transition-colors duration-200 mb-2">Studio</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4">
                Purpose-built decision surface for senior dealmakers. Replaces manual spreadsheets with live IC committee rubrics, scenario trees, and adversarial debates.
              </p>
            </div>
            <div className="pt-1 text-xs text-slate-500 group-hover:text-[#F58220] transition-colors flex items-center gap-1 font-sans">
              <span>&#8627; Read more</span>
            </div>
          </div>

          {/* Card 4: Memo & Model */}
          <div onClick={() => goToProduct('#memo')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && goToProduct('#memo')} className="flex flex-col justify-between group cursor-pointer">
            <div className="w-full h-[380px] sm:h-[410px] rounded-2xl bg-gradient-to-b from-[#F7F8FA] via-[#ECEEF2] to-[#E2E5EB] border border-slate-200/80 group-hover:border-[#F58220]/50 group-hover:shadow-[0_16px_36px_-12px_rgba(245,130,32,0.18)] overflow-hidden relative flex flex-col items-center pt-5 px-4 mb-5 transition-all duration-300">
              <div className="w-full bg-white rounded-2xl shadow-xl border border-black/5 group-hover:border-[#F58220]/30 p-4 space-y-2.5 font-sans transform group-hover:translate-y-1.5 transition duration-300">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#F58220]"></span>
                    <span className="text-[10px] font-bold text-slate-800">Diligence</span>
                  </div>
                  <span className="text-[9px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">+ Add Item</span>
                </div>

                <div className="space-y-1.5 text-[10px]">
                  <div className="flex items-center justify-between py-1 border-b border-slate-50">
                    <div className="flex items-center gap-2 truncate">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="text-slate-700 truncate">Ingest &amp; reconcile raw VDR</span>
                    </div>
                    <span className="text-[8px] font-mono text-slate-400 shrink-0">1,420 files</span>
                  </div>

                  <div className="flex items-center justify-between p-1.5 rounded-lg bg-blue-50/80 border border-blue-200/70 text-blue-900 group-hover:border-[#F58220]/40 transition duration-200">
                    <div className="flex items-center gap-2 truncate font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span className="truncate">Synthesize 48h Strategic Diligence Deck</span>
                    </div>
                    <span className="text-[8px] font-mono text-blue-700 font-bold shrink-0">4.51x MOIC</span>
                  </div>

                  <div className="flex items-center justify-between py-1 border-b border-slate-50">
                    <div className="flex items-center gap-2 truncate">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="text-slate-700 truncate">Deterministic LBO Math</span>
                    </div>
                    <span className="text-[8px] font-mono text-slate-400 shrink-0">Off-LLM</span>
                  </div>

                  <div className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2 truncate">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="text-slate-700 truncate">Cell-to-source audit lineage</span>
                    </div>
                    <span className="text-[8px] font-mono text-emerald-600 font-bold shrink-0">100%</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-[#0F172A] group-hover:text-[#F58220] transition-colors duration-200 mb-2">Memo &amp; Model</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4">
                Audit-ready diligence slide decks, strategic memos, and deterministic financial models synthesized directly from raw data rooms in your firm&#8217;s exact institutional format.
              </p>
            </div>
            <div className="pt-1 text-xs text-slate-500 group-hover:text-[#F58220] transition-colors flex items-center gap-1 font-sans">
              <span>&#8627; Read more</span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
